import useUserSettings from '@/hooks/use-user-settings.hook';
import { Box, Slide, Stack, Theme } from '@mui/material';
import { FC, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import ToolbarButton from './button';
import ToolbarInfoRow from './info-row';
import { getNiceRecentDate } from '@/lib/date-helpers';
import { TileModel } from '@/lib/models/user-settings/tile.model';

interface Props {
  tile: TileModel;
  open: boolean;
  onChange: () => void;
  showInfo?: boolean;
}

const useStyles = createUseStyles<string, { hasButtons: boolean }>((theme: Theme) => ({
  tileToolbar: {
    position: 'absolute',
    height: 100,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.default,
    padding: 8,
    borderTop: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}`,
    color: theme.palette.text.primary,
  },
  tileInfo: {
    paddingBottom: 8,
    marginBottom: 8,
    borderBottom: ({ hasButtons }) =>
      hasButtons
        ? `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}`
        : 'none',
  },
}));

const TileToolbar: FC<Props> = ({ tile, open, onChange, showInfo = true }) => {
  const { typeLabel, recent } = tile;
  const hasRecent = !!recent;
  const hasButtons = hasRecent;

  const styles = useStyles({ hasButtons });

  const recentDate = useMemo(() => {
    return recent ? getNiceRecentDate(recent) : '';
  }, [recent]);

  const { toggleRecent } = useUserSettings();

  const handleRemoveRecent = () => {
    toggleRecent(tile, false);
    onChange();
  };

  return (
    <Slide
      direction="up"
      in={open}
      mountOnEnter
      unmountOnExit
      timeout={230}
      className={styles.tileToolbar}
    >
      <Box>
        {showInfo && (
          <Stack gap={0.5} className={styles.tileInfo}>
            <ToolbarInfoRow label="Type" value={typeLabel} />
            <ToolbarInfoRow label="Last Used" value={recentDate} />
          </Stack>
        )}
        <Stack
          gap={1}
          width="100%"
          height="100%"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          {hasRecent && (
            <ToolbarButton onClick={handleRemoveRecent} label="Remove from Recent" icon="delete" />
          )}
        </Stack>
      </Box>
    </Slide>
  );
};

export default TileToolbar;
